import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import classnames from "classnames";
import {
  getPayments,
  createPayment,
  updatePayment,
  deletePayment,
  updateCredit,
  getPlacedOrders,
  getProspectOrganizations,
} from "./service";
import { formatDateWithSuffix } from "../common/DateHelpers";

const PaymentsCreditsManager = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState("");
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState("");
  const [orderOutstandingSortDir, setOrderOutstandingSortDir] = useState(null);

  const modeMapper = {
    cash: "Cash",
    bank_transfer: "Bank Transfer",
    credit_note: "Credit Note",
  };

  const toggleOrderOutstandingSort = () => {
    setOrderOutstandingSortDir((prev) => {
      if (prev === "asc") return "desc";
      if (prev === "desc") return "asc";
      return "asc"; // first click: asc
    });
  };

  const [formData, setFormData] = useState({
    order_id: "",
    organization_id: "",
    value: 0,
    credit_limit: 0,
    credit_duration_days: 0,
    date: "",
    mode: "cash",
    remarks: "",
    order_name: "",
    organization_name: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [paymentsResponse, ordersResponse, organizationsResponse] =
        await Promise.all([
          getPayments(),
          getPlacedOrders(),
          getProspectOrganizations(),
        ]);

      setPayments(paymentsResponse || []);
      setOrders(ordersResponse.orders || []);
      setOrganizations(organizationsResponse.organizations || []);
    } catch (error) {
      showAlert(
        "Error fetching data: " +
          (error.response?.data?.message || error.message),
        "danger"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 3000);
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setSelectedOrder(null);
      setSelectedOrganization(null);
    }
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    setSelectedOrganization(null);
  };

  const handleOrganizationSelect = (organization) => {
    setSelectedOrganization(organization);
    setSelectedOrder(null);
  };

  const getPaymentsForOrder = (orderId) => {
    return payments.filter(
      (payment) => payment && payment.order_id === orderId
    );
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);

    // Reset form data first
    setFormData({
      order_id: "",
      organization_id: "",
      value: 0,
      credit_limit: 0,
      credit_duration_days: 0,
      date: "",
      mode: "cash",
      remarks: "",
      order_name: "",
      organization_name: "",
    });

    if (item) {
      let paymentDate = "";

      if (type === "payment") {
        let rawDate = item.payment_date || item.date || "";

        if (rawDate) {
          try {
            let dateObj;
            if (typeof rawDate === "string") {
              if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
                paymentDate = rawDate;
              } else {
                dateObj = new Date(rawDate);
              }
            } else if (rawDate instanceof Date) {
              dateObj = rawDate;
            } else {
              dateObj = new Date(rawDate);
            }

            if (dateObj && !isNaN(dateObj.getTime())) {
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, "0");
              const day = String(dateObj.getDate()).padStart(2, "0");
              paymentDate = `${year}-${month}-${day}`;
            } else {
              paymentDate = rawDate;
            }
          } catch (e) {
            paymentDate = rawDate;
          }
        }
      }

      const finalFormData = {
        order_id: item.order_id || "",
        organization_id: item.organization_id || "",
        value: parseInt(item.value || 0, 10),
        credit_limit: parseInt(item.credit_limit || 0, 10),
        credit_duration_days: parseInt(item.credit_duration_days || 0, 10),
        date: paymentDate,
        order_name: item.order_name || "",
        organization_name: item.organization_name || "",
        mode: type === "payment" ? item.mode || "" : "cash",
        remarks: type === "payment" ? item.remarks || "" : "",
      };

      setFormData(finalFormData);
    } else {
      if (type === "payment" && selectedOrder) {
        setFormData({
          order_id: selectedOrder.id,
          organization_id: "",
          value: 0,
          credit_limit: 0,
          credit_duration_days: 0,
          date: "",
          mode: "cash",
          remarks: "",
          order_name: selectedOrder.name,
          organization_name: "",
        });
      } else if (type === "credit" && selectedOrganization) {
        setFormData({
          order_id: "",
          organization_id: selectedOrganization.id,
          value: 0,
          credit_limit: 0,
          credit_duration_days: 0,
          date: "",
          mode: "cash",
          remarks: "",
          order_name: "",
          organization_name: selectedOrganization.name,
        });
      } else {
        setFormData({
          order_id: "",
          organization_id: "",
          value: 0,
          credit_limit: 0,
          credit_duration_days: 0,
          date: "",
          mode: "cash",
          remarks: "",
          order_name: "",
          organization_name: "",
        });
      }
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({
      order_id: "",
      organization_id: "",
      value: 0,
      credit_limit: 0,
      credit_duration_days: 0,
      date: "",
      mode: "cash",
      remarks: "",
      order_name: "",
      organization_name: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (
      name === "value" ||
      name === "credit_limit" ||
      name === "credit_duration_days"
    ) {
      const intValue = value === "" ? "" : parseInt(value, 10);
      if (isNaN(intValue) && value !== "") {
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: intValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async () => {
    if (modalType === "payment") {
      if (!formData.value || formData.value <= 0) {
        showAlert(
          "Please enter a valid payment amount greater than 0",
          "danger"
        );
        return;
      }
      if (!editingItem && !formData.order_id) {
        showAlert("Please select an order", "danger");
        return;
      }
      if (!formData.mode) {
        showAlert("Please select a payment mode", "danger");
        return;
      }
      if (!formData.date) {
        showAlert("Please enter payment date", "danger");
        return;
      }
    } else {
      if (!formData.credit_limit || formData.credit_limit <= 0) {
        showAlert("Please enter a valid credit limit greater than 0", "danger");
        return;
      }
      if (
        !formData.credit_duration_days ||
        formData.credit_duration_days <= 0
      ) {
        showAlert(
          "Please enter a valid credit duration (days) greater than 0",
          "danger"
        );
        return;
      }
      if (!editingItem && !formData.organization_id) {
        showAlert("Please select an organization", "danger");
        return;
      }
    }

    try {
      if (editingItem) {
        if (modalType === "payment") {
          const updatePayload = {
            order_id: editingItem.order_id,
            value: formData.value,
            date: formData.date,
            mode: formData.mode,
            remarks: formData.remarks,
          };

          const response = await updatePayment(editingItem.id, updatePayload);

          // Update local state directly instead of calling fetchData()
          setPayments((prevPayments) =>
            prevPayments.map((payment) =>
              payment.id === editingItem.id
                ? {
                    ...payment,
                    value: formData.value,
                    date: formData.date,
                    payment_date: formData.date,
                    mode: formData.mode,
                    remarks: formData.remarks,
                  }
                : payment
            )
          );

          showAlert("Payment updated successfully");
        } else {
          const updatePayload = {
            organization_id: editingItem.id,
            credit_limit: formData.credit_limit,
            credit_duration_days: formData.credit_duration_days,
          };

          const response = await updateCredit(editingItem.id, updatePayload);

          // Update local state directly instead of calling fetchData()
          setOrganizations((prevOrgs) =>
            prevOrgs.map((org) =>
              org.id === editingItem.id
                ? {
                    ...org,
                    credit_limit: formData.credit_limit,
                    credit_duration_days: formData.credit_duration_days,
                  }
                : org
            )
          );

          // Update selected organization if it's the one being edited
          if (
            selectedOrganization &&
            selectedOrganization.id === editingItem.id
          ) {
            setSelectedOrganization((prev) => ({
              ...prev,
              credit_limit: formData.credit_limit,
              credit_duration_days: formData.credit_duration_days,
            }));
          }

          showAlert("Credit updated successfully");
        }
      } else {
        if (modalType === "payment") {
          const createPayload = {
            order_id: formData.order_id,
            value: formData.value,
            date: formData.date,
            mode: formData.mode,
            remarks: formData.remarks,
          };

          const response = await createPayment(createPayload);

          // Add new payment to local state directly instead of calling fetchData()
          // Handle different possible response structures
          let newPayment;
          if (response && response.data) {
            // If response has data property
            newPayment = {
              ...response.data,
              order_id: formData.order_id,
              value: formData.value,
              date: formData.date,
              payment_date: formData.date,
              mode: response.data.mode || formData.mode,
              remarks: response.data.remarks || formData.remarks,
            };
          } else if (response && response.id) {
            // If response is the payment object directly
            newPayment = {
              ...response,
              order_id: formData.order_id,
              value: formData.value,
              date: formData.date,
              payment_date: formData.date,
              mode: response.mode || formData.mode,
              remarks: response.remarks || formData.remarks,
            };
          } else {
            // Fallback: create payment object with form data
            newPayment = {
              id: Date.now(), // Temporary ID
              order_id: formData.order_id,
              value: formData.value,
              date: formData.date,
              payment_date: formData.date,
              mode: formData.mode,
              remarks: formData.remarks,
            };
          }

          setPayments((prevPayments) => [...prevPayments, newPayment]);
          showAlert("Payment added successfully");
        } else {
          showAlert(
            "Credits can only be updated. Please select an existing organization.",
            "warning"
          );
          return;
        }
      }
      closeModal();
    } catch (error) {
      showAlert(
        "Error saving data: " +
          (error.response?.data?.message || error.message),
        "danger"
      );
    }
  };

  const openDeleteModal = (type, item) => {
    setItemToDelete({ type, item });
    setDeleteModalOpen(true);
  };

  const ensureAmountIsNumber = (amount) => {
    if (typeof amount === "string") {
      return parseInt(amount, 10) || 0;
    }
    return amount || 0;
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === "payment") {
        await deletePayment(itemToDelete.item.id);

        // Remove payment from local state directly instead of calling fetchData()
        setPayments((prevPayments) =>
          prevPayments.filter((payment) => payment.id !== itemToDelete.item.id)
        );
      }

      showAlert(
        `${
          itemToDelete.type === "payment" ? "Payment" : "Credit"
        } deleted successfully`
      );
      closeDeleteModal();
    } catch (error) {
      showAlert(
        "Error deleting data: " +
          (error.response?.data?.message || error.message),
        "danger"
      );
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTotalAmount = (items) => {
    return items.reduce(
      (sum, item) => sum + ensureAmountIsNumber(item.value),
      0
    );
  };

  const getOrganizationTotalCredits = (organization) => {
    return organization.credit_limit || 0;
  };

  return (
    <Container fluid className="p-4">
      <style>
        {`
          .table-row-red {
            background-color: #ff0000 !important;
            color: white !important;
          }
          .table-row-red td {
            background-color: #ff0000 !important;
            color: white !important;
          }
          .table-row-yellow {
            background-color: #ffff00 !important;
            color: black !important;
          }
          .table-row-yellow td {
            background-color: #ffff00 !important;
            color: black !important;
          }
          .table thead {
            background-color: #6c757d !important;
            color: white !important;
          }
          .table thead th {
            background-color: #6c757d !important;
            color: white !important;
            border-color: #6c757d !important;
          }
          .custom-header {
            background-color: #6c757d !important;
            color: white !important;
          }
          .custom-header th {
            background-color: #6c757d !important;
            color: white !important;
            border-color: #6c757d !important;
          }
        `}
      </style>

      {alert.show && (
        <Alert color={alert.type} className="mb-3">
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Payments & Credits Manager</h4>
          </div>

          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "1" })}
                onClick={() => toggleTab("1")}
              >
                <i className="fas fa-credit-card me-1"></i>
                Payments
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "2" })}
                onClick={() => toggleTab("2")}
              >
                <i className="fas fa-gift me-1"></i>
                Credits
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <div className="mt-3">
                {isLoading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : selectedOrder ? (
                  <Row>
                    {/* Left Side - Orders List */}
                    <Col md={4}>
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-start align-items-center mb-3">
                            <h6 className="mb-0">Orders</h6>
                            <span className="badge bg-primary ms-2">
                              {
                                orders.filter((order) => {
                                  const outstandingAmount =
                                    order.outstanding || 0;
                                  const orderValue = order.order_value || 0;
                                  return !(
                                    outstandingAmount === 0 && orderValue === 0
                                  );
                                }).length
                              }
                            </span>
                          </div>
                          <div style={{ maxHeight: "75vh", overflowY: "auto" }}>
                            <ListGroup>
                              {orders
                                .filter((order) => {
                                  const outstandingAmount =
                                    order.outstanding || 0;
                                  const orderValue = order.order_value || 0;
                                  return !(
                                    outstandingAmount === 0 && orderValue === 0
                                  );
                                })
                                .map((order) => (
                                  <ListGroupItem
                                    key={order.id}
                                    action
                                    active={selectedOrder?.id === order.id}
                                    onClick={() => handleOrderSelect(order)}
                                    className="d-flex justify-content-between align-items-center"
                                  >
                                    <div className="text-truncate me-2">
                                      {order.name}
                                    </div>
                                    <span className="badge bg-secondary">
                                      {getPaymentsForOrder(order.id).length}
                                    </span>
                                  </ListGroupItem>
                                ))}
                            </ListGroup>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    {/* Right Side - Payments for Selected Order */}
                    <Col md={8}>
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <h6 className="mb-1">{selectedOrder.name}</h6>
                              <small className="text-muted">
                                Customer: {selectedOrder.customer} | ID:{" "}
                                {selectedOrder.id} | Identifier:{" "}
                                {selectedOrder.identifier} | Status:{" "}
                                {selectedOrder.order_status} | Order Value:{" "}
                                {formatCurrency(selectedOrder.order_value || 0)}
                              </small>
                            </div>
                            <div>
                              <Button
                                color="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => openModal("payment")}
                              >
                                <i className="fas fa-plus me-1"></i>
                                Add Payment
                              </Button>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={() => setSelectedOrder(null)}
                              >
                                <i className="fas fa-times me-1"></i>
                                Close
                              </Button>
                            </div>
                          </div>

                          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                            <Table responsive striped>
                              <thead
                                style={{
                                  backgroundColor: "#6c757d",
                                  color: "white",
                                }}
                              >
                                <tr>
                                  <th className="text-end">Amount</th>
                                  <th className="text-center">Payment Date</th>
                                  <th className="text-center">Mode</th>
                                  <th className="text-center">Remarks</th>
                                  <th className="text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getPaymentsForOrder(selectedOrder.id).map(
                                  (payment) => (
                                    <tr key={payment?.id || Math.random()}>
                                      <td className="fw-bold text-end">
                                        {formatCurrency(payment?.value || 0)}
                                      </td>
                                      <td className="text-center">
                                        {formatDateWithSuffix(
                                          payment?.payment_date ||
                                            payment?.date ||
                                            new Date()
                                        )}
                                      </td>
                                      <td className="text-center">
                                        {modeMapper[payment.mode] || "-"}
                                      </td>
                                      <td className="text-center">
                                        {payment.remarks || "-"}
                                      </td>
                                      <td className="text-center">
                                        <Button
                                          color="info"
                                          size="sm"
                                          className="me-1"
                                          onClick={() =>
                                            openModal("payment", payment)
                                          }
                                        >
                                          <i className="fas fa-edit"></i>
                                        </Button>
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() =>
                                            openDeleteModal("payment", payment)
                                          }
                                        >
                                          <i className="fas fa-trash"></i>
                                        </Button>
                                      </td>
                                    </tr>
                                  )
                                )}
                                {getPaymentsForOrder(selectedOrder.id)
                                  .length === 0 && (
                                  <tr>
                                    <td
                                      colSpan="3"
                                      className="text-center text-muted py-4"
                                    >
                                      No payments found for this order
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>

                          {getPaymentsForOrder(selectedOrder.id).length > 0 && (
                            <div className="text-end">
                              <strong>
                                Total:{" "}
                                {formatCurrency(
                                  getTotalAmount(
                                    getPaymentsForOrder(selectedOrder.id)
                                  )
                                )}
                              </strong>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex justify-content-start align-items-center">
                          <h6 className="mb-0">All Orders</h6>
                          <span className="badge bg-primary ms-2">
                            {(() => {
                              const filtered = orders.filter((order) => {
                                const outstandingAmount =
                                  order.outstanding || 0;
                                const orderValue = order.order_value || 0;
                                const passesBaseFilter = !(
                                  outstandingAmount === 0 && orderValue === 0
                                );
                                const passesCustomerFilter =
                                  !selectedCustomerFilter ||
                                  order.customer === selectedCustomerFilter;
                                return passesBaseFilter && passesCustomerFilter;
                              });
                              return filtered.length;
                            })()}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <FormGroup className="mb-0">
                            <Label
                              for="customerFilter"
                              className="me-2 mb-0"
                              style={{ fontSize: "0.9rem" }}
                            >
                              Filter by Customer:
                            </Label>
                            <Input
                              type="select"
                              id="customerFilter"
                              name="customerFilter"
                              value={selectedCustomerFilter}
                              onChange={(e) =>
                                setSelectedCustomerFilter(e.target.value)
                              }
                              style={{
                                width: "200px",
                                display: "inline-block",
                              }}
                            >
                              <option value="">All Customers</option>
                              {[
                                ...new Set(
                                  orders
                                    .map((order) => order.customer)
                                    .filter(Boolean)
                                ),
                              ]
                                .sort()
                                .map((customer) => (
                                  <option key={customer} value={customer}>
                                    {customer}
                                  </option>
                                ))}
                            </Input>
                          </FormGroup>
                          <div className="text-end">
                            <strong>
                              Total Payments:{" "}
                              {formatCurrency(
                                payments.reduce(
                                  (sum, payment) =>
                                    sum + ensureAmountIsNumber(payment.value),
                                  0
                                )
                              )}
                            </strong>
                          </div>
                        </div>
                      </div>
                      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
                        <Table responsive striped>
                          <thead
                            className="custom-header"
                            style={{
                              backgroundColor: "#6c757d",
                              color: "white",
                            }}
                          >
                            <tr>
                              <th>Order Name</th>
                              <th>Customer</th>
                              <th className="text-center">Identifier</th>
                              <th className="text-end">Order Value</th>
                              <th className="text-end">Total Payments</th>
                              <th
                                className="text-end"
                                onClick={toggleOrderOutstandingSort}
                                style={{
                                  cursor: "pointer",
                                  userSelect: "none",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Outstanding{" "}
                                {orderOutstandingSortDir ? (
                                  <span style={{ fontSize: "0.85em" }}>
                                    ({orderOutstandingSortDir})
                                  </span>
                                ) : null}
                              </th>
                              <th className="text-center">Placed On</th>
                              <th className="text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const filteredOrders = orders.filter((order) => {
                                const outstandingAmount =
                                  order.outstanding || 0;
                                const orderValue = order.order_value || 0;
                                const passesBaseFilter = !(
                                  outstandingAmount === 0 && orderValue === 0
                                );
                                const passesCustomerFilter =
                                  !selectedCustomerFilter ||
                                  order.customer === selectedCustomerFilter;
                                return passesBaseFilter && passesCustomerFilter;
                              });

                              const totals = filteredOrders.reduce(
                                (acc, order) => {
                                  const orderPayments = getPaymentsForOrder(
                                    order.id
                                  );
                                  const totalPayments =
                                    getTotalAmount(orderPayments);
                                  const outstandingAmount =
                                    order.outstanding || 0;
                                  const orderValue = order.order_value || 0;

                                  return {
                                    orderValue: acc.orderValue + orderValue,
                                    totalPayments:
                                      acc.totalPayments + totalPayments,
                                    outstanding:
                                      acc.outstanding + outstandingAmount,
                                  };
                                },
                                {
                                  orderValue: 0,
                                  totalPayments: 0,
                                  outstanding: 0,
                                }
                              );

                              const sortedOrders = (() => {
                                if (!orderOutstandingSortDir) {
                                  return filteredOrders;
                                }

                                return filteredOrders
                                  .map((order, index) => ({ order, index }))
                                  .sort((a, b) => {
                                    const aVal = ensureAmountIsNumber(
                                      a.order.outstanding
                                    );
                                    const bVal = ensureAmountIsNumber(
                                      b.order.outstanding
                                    );

                                    if (aVal === bVal) {
                                      // Stable tie-break using original filtered order.
                                      return a.index - b.index;
                                    }

                                    return orderOutstandingSortDir === "asc"
                                      ? aVal - bVal
                                      : bVal - aVal;
                                  })
                                  .map(({ order }) => order);
                              })();

                              return (
                                <>
                                  {filteredOrders.length > 0 && (
                                    <tr
                                      style={{
                                        backgroundColor: "#e9ecef",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      <td colSpan="3" className="text-end">
                                        <strong>Totals:</strong>
                                      </td>
                                      <td className="fw-bold text-end">
                                        {formatCurrency(totals.orderValue)}
                                      </td>
                                      <td className="fw-bold text-end">
                                        {formatCurrency(totals.totalPayments)}
                                      </td>
                                      <td className="fw-bold text-end">
                                        {formatCurrency(totals.outstanding)}
                                      </td>
                                      <td colSpan="2"></td>
                                    </tr>
                                  )}
                                  {sortedOrders.map((order) => {
                                    const orderPayments = getPaymentsForOrder(
                                      order.id
                                    );
                                    const totalPayments =
                                      getTotalAmount(orderPayments);
                                    const outstandingAmount =
                                      order.outstanding || 0;
                                    const orderValue = order.order_value || 0;
                                    return (
                                      <tr
                                        key={order.id}
                                        className="cursor-pointer"
                                        onClick={() => handleOrderSelect(order)}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <td className="fw-bold">
                                          {order.name}
                                        </td>
                                        <td>{order.customer}</td>
                                        <td className="text-center">
                                          {order.identifier || "-"}
                                        </td>
                                        <td className="fw-bold text-end">
                                          {formatCurrency(orderValue)}
                                        </td>
                                        <td className="fw-bold text-end">
                                          {formatCurrency(totalPayments)}
                                        </td>
                                        <td className="text-end">
                                          {formatCurrency(outstandingAmount)}
                                        </td>
                                        <td className="text-center">
                                          {order.placed_on
                                            ? formatDateWithSuffix(
                                                order.placed_on
                                              )
                                            : "-"}
                                        </td>
                                        <td className="text-center">
                                          {order.order_status}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {filteredOrders.length === 0 && (
                                    <tr>
                                      <td
                                        colSpan="8"
                                        className="text-center text-muted py-4"
                                      >
                                        No orders found
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            })()}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            </TabPane>

            <TabPane tabId="2">
              <div className="mt-3">
                {isLoading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : selectedOrganization ? (
                  <Row style={{ maxHeight: "80vh", overflowY: "auto" }}>
                    {/* Left Side - Organizations List */}
                    <Col md={4}>
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-start align-items-center mb-3">
                            <h6 className="mb-0">Organizations</h6>
                            <span className="badge bg-success ms-2">
                              {organizations.length}
                            </span>
                          </div>
                          <div style={{ maxHeight: "75vh", overflowY: "auto" }}>
                            <ListGroup>
                              {organizations.map((organization) => (
                                <ListGroupItem
                                  key={organization.id}
                                  action
                                  active={
                                    selectedOrganization?.id === organization.id
                                  }
                                  onClick={() =>
                                    handleOrganizationSelect(organization)
                                  }
                                  className="d-flex justify-content-between align-items-center"
                                >
                                  <div className="text-truncate me-2">
                                    {organization.name}
                                  </div>
                                  <span className="badge bg-secondary">
                                    {formatCurrency(
                                      organization.credit_limit || 0
                                    )}
                                  </span>
                                </ListGroupItem>
                              ))}
                            </ListGroup>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    {/* Right Side - Credits for Selected Organization */}
                    <Col md={8}>
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <h6 className="mb-1">
                                {selectedOrganization.name}
                              </h6>
                              <small className="text-muted">
                                Organization ID: {selectedOrganization.id}
                              </small>
                            </div>
                            <div>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={() => setSelectedOrganization(null)}
                              >
                                <i className="fas fa-times me-1"></i>
                                Close
                              </Button>
                            </div>
                          </div>

                          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                            <Table responsive striped>
                              <thead
                                style={{
                                  backgroundColor: "#6c757d",
                                  color: "white",
                                }}
                              >
                                <tr>
                                  <th className="text-end">Credit Limit</th>
                                  <th className="text-center">
                                    Credit Duration (Days)
                                  </th>
                                  <th className="text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="fw-bold text-end">
                                    {formatCurrency(
                                      selectedOrganization.credit_limit || 0
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {selectedOrganization.credit_duration_days ||
                                      0}
                                  </td>
                                  <td className="text-center">
                                    <Button
                                      color="info"
                                      size="sm"
                                      className="me-1"
                                      onClick={() =>
                                        openModal(
                                          "credit",
                                          selectedOrganization
                                        )
                                      }
                                    >
                                      <i className="fas fa-edit"></i>
                                    </Button>
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-start align-items-center mb-3">
                        <h6 className="mb-0">All Organizations</h6>
                        <span className="badge bg-success ms-2">
                          {organizations.length}
                        </span>
                      </div>
                      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
                        <Table responsive>
                          <thead
                            className="custom-header"
                            style={{
                              backgroundColor: "#6c757d",
                              color: "white",
                            }}
                          >
                            <tr>
                              <th>Organization</th>
                              <th className="text-end">Credit Limit</th>
                              <th className="text-center">
                                Credit Duration (Days)
                              </th>
                              <th className="text-end">Outstanding</th>
                            </tr>
                          </thead>
                          <tbody>
                            {organizations.map((organization) => {
                              const outstandingAmount =
                                organization.outstanding || 0;
                              const totalCredits =
                                organization.credit_limit || 0;
                              const calculatedTotalCredits =
                                getOrganizationTotalCredits(organization);

                              let rowClass = "cursor-pointer";
                              let cellStyle = {};

                              if (outstandingAmount > totalCredits) {
                                rowClass += " table-row-red";
                                cellStyle = {
                                  backgroundColor: "#ff0000",
                                  color: "white",
                                };
                              } else if (
                                outstandingAmount >=
                                totalCredits * 0.9
                              ) {
                                rowClass += " table-row-yellow";
                                cellStyle = {
                                  backgroundColor: "#ffff00",
                                  color: "black",
                                };
                              }

                              return (
                                <tr
                                  key={organization.id}
                                  className={rowClass}
                                  onClick={() =>
                                    handleOrganizationSelect(organization)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <td className="fw-bold" style={cellStyle}>
                                    {organization.name}
                                  </td>
                                  <td
                                    className="fw-bold text-end"
                                    style={cellStyle}
                                  >
                                    {formatCurrency(
                                      organization.credit_limit || 0
                                    )}
                                  </td>
                                  <td
                                    className="fw-bold text-center"
                                    style={cellStyle}
                                  >
                                    {organization.credit_duration_days || 0}
                                  </td>
                                  <td className="text-end" style={cellStyle}>
                                    {formatCurrency(outstandingAmount)}
                                  </td>
                                </tr>
                              );
                            })}
                            {organizations.length === 0 && (
                              <tr>
                                <td
                                  colSpan="4"
                                  className="text-center text-muted py-4"
                                >
                                  No organizations found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          {editingItem ? "Edit" : "Add"}{" "}
          {modalType === "payment" ? "Payment" : "Credit"}
        </ModalHeader>
        <ModalBody>
          <Form>
            {modalType === "payment" ? (
              editingItem ? (
                <FormGroup>
                  <Label>Order</Label>
                  <div className="form-control-plaintext">
                    {formData.order_name || `Order #${formData.order_id}`}
                  </div>
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label>Order</Label>
                  <div className="form-control-plaintext">
                    {selectedOrder?.name || "No order selected"}
                  </div>
                </FormGroup>
              )
            ) : editingItem ? (
              <FormGroup>
                <Label>Organization</Label>
                <div className="form-control-plaintext">
                  {formData.organization_name ||
                    `Organization #${formData.organization_id}`}
                </div>
              </FormGroup>
            ) : (
              <FormGroup>
                <Label>Organization</Label>
                <div className="form-control-plaintext">
                  {selectedOrganization?.name || "No organization selected"}
                </div>
              </FormGroup>
            )}

            {modalType === "payment" ? (
              <>
                <FormGroup>
                  <Label for="value">Payment Amount *</Label>
                  <Input
                    type="number"
                    name="value"
                    id="value"
                    value={formData.value}
                    onChange={handleInputChange}
                    placeholder="Enter payment amount"
                    min="0"
                    step="1"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="mode">Payment Mode *</Label>
                  <Input
                    type="select"
                    name="mode"
                    id="mode"
                    value={formData.mode}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select mode</option>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="credit_note">Credit Note</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="remarks">Remarks</Label>
                  <Input
                    type="textarea"
                    name="remarks"
                    id="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    placeholder="Optional"
                  />
                </FormGroup>
              </>
            ) : (
              <>
                <FormGroup>
                  <Label for="credit_limit">Credit Limit *</Label>
                  <Input
                    type="number"
                    name="credit_limit"
                    id="credit_limit"
                    value={formData.credit_limit}
                    onChange={handleInputChange}
                    placeholder="Enter credit limit"
                    min="0"
                    step="1"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="credit_duration_days">
                    Credit Duration (Days) *
                  </Label>
                  <Input
                    type="number"
                    name="credit_duration_days"
                    id="credit_duration_days"
                    value={formData.credit_duration_days}
                    onChange={handleInputChange}
                    placeholder="Enter credit duration in days"
                    min="0"
                    step="1"
                    required
                  />
                </FormGroup>
              </>
            )}

            {modalType === "payment" && (
              <FormGroup>
                <Label for="date">Payment Date *</Label>
                <Input
                  type="date"
                  name="date"
                  id="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            {editingItem ? "Update" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={closeDeleteModal} size="sm">
        <ModalHeader toggle={closeDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this{" "}
          {itemToDelete?.type === "payment" ? "payment" : "credit"}?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default PaymentsCreditsManager;
